import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ── Date helpers ─────────────────────────────────────────────────────────────
function getWeekBounds(offsetWeeks = 0) {
  const now = new Date();
  const day = now.getDay(); // 0=Sun
  const mon = new Date(now);
  mon.setDate(now.getDate() - (day === 0 ? 6 : day - 1) + offsetWeeks * 7);
  mon.setHours(0, 0, 0, 0);
  const fri = new Date(mon);
  fri.setDate(mon.getDate() + 6);
  const fmt = (d) => d.toISOString().split('T')[0];
  return { from: fmt(mon), to: fmt(fri) };
}

function fmtWeekLabel(from, to) {
  const f = new Date(from + 'T12:00:00');
  const t = new Date(to   + 'T12:00:00');
  const opts = { month: 'short', day: 'numeric' };
  return f.toLocaleDateString('en-US', opts) + ' – ' + t.toLocaleDateString('en-US', opts);
}

function fmtDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function fmtTime(isoStr) {
  if (!isoStr) return '—';
  const d = new Date(isoStr);
  if (isNaN(d)) return isoStr;
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZoneName: 'short' });
}

const IMPACT_COLOR = { high: '#ef4444', medium: '#ffaa00', low: '#64748b' };
const IMPACT_LABEL = { high: '🔴 High', medium: '🟡 Med', low: '⚪ Low' };

const TABS = [
  { id: 'earnings', label: '📈 Earnings' },
  { id: 'economic', label: '🏛 Economic' },
  { id: 'ipo',      label: '🚀 IPO'      },
];

// ── Sub-components ───────────────────────────────────────────────────────────
const EmptyState = ({ msg }) => (
  <div style={{ textAlign: 'center', padding: '3rem 1rem',
    color: '#64748b', fontSize: '0.88rem' }}>
    {msg}
  </div>
);

const LoadingState = () => (
  <div style={{ textAlign: 'center', padding: '3rem 1rem',
    color: '#64748b', fontSize: '0.88rem' }}>
    ⏳ Loading…
  </div>
);

// ── Watchlist sidebar ────────────────────────────────────────────────────────
function WatchlistPanel({ isLoggedIn, token, filterTickers, setFilterTickers, onRequestInvite, onSignIn }) {
  const [wlTickers, setWlTickers] = useState([]);

  useEffect(() => {
    if (!token) return;
    fetch(API_URL + '/api/watchlist', {
      headers: { Authorization: 'Bearer ' + token }
    }).then(r => r.json()).then(d => {
      if (d.success) setWlTickers(d.tickers || []);
    }).catch(() => {});
  }, [token]);

  if (!isLoggedIn) {
    return (
      <div style={{ padding: '1.25rem', background: 'rgba(0,0,0,0.3)',
        border: '1px solid rgba(255,255,255,0.06)', borderRadius: '12px' }}>
        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569',
          letterSpacing: '0.08em', marginBottom: '0.85rem' }}>
          MY WATCHLIST FILTER 🔒
        </div>
        <div style={{ fontSize: '0.8rem', color: '#64748b', lineHeight: 1.6, marginBottom: '1rem' }}>
          Filter this calendar to only show events for stocks in your watchlist.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <button onClick={onSignIn}
            style={{ padding: '0.6rem 1rem',
              background: 'linear-gradient(135deg,#00ff88,#00d4ff)',
              border: 'none', borderRadius: '8px', color: '#050d1a',
              fontWeight: 800, cursor: 'pointer', fontSize: '0.82rem',
              fontFamily: 'inherit' }}>
            Sign In
          </button>
          <button onClick={onRequestInvite}
            style={{ padding: '0.6rem 1rem', background: 'transparent',
              border: '1px solid rgba(0,255,136,0.3)', borderRadius: '8px',
              color: '#00ff88', fontWeight: 700, cursor: 'pointer',
              fontSize: '0.82rem', fontFamily: 'inherit' }}>
            Request Invite
          </button>
        </div>
      </div>
    );
  }

  if (wlTickers.length === 0) {
    return (
      <div style={{ padding: '1.25rem', background: 'rgba(255,255,255,0.02)',
        border: '1px solid rgba(255,255,255,0.06)', borderRadius: '12px' }}>
        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569',
          letterSpacing: '0.08em', marginBottom: '0.85rem' }}>MY WATCHLIST FILTER</div>
        <div style={{ fontSize: '0.78rem', color: '#64748b' }}>
          Add stocks to your watchlist to filter the calendar.
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '1.25rem', background: 'rgba(255,255,255,0.02)',
      border: '1px solid rgba(255,255,255,0.06)', borderRadius: '12px' }}>
      <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569',
        letterSpacing: '0.08em', marginBottom: '0.85rem' }}>MY WATCHLIST FILTER</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.75rem' }}>
        {wlTickers.map(t => {
          const active = filterTickers.includes(t);
          return (
            <button key={t}
              onClick={() => setFilterTickers(prev =>
                active ? prev.filter(x => x !== t) : [...prev, t]
              )}
              style={{ padding: '0.3rem 0.7rem',
                background: active ? 'rgba(0,212,255,0.15)' : 'rgba(255,255,255,0.04)',
                border: active ? '1px solid rgba(0,212,255,0.4)' : '1px solid rgba(255,255,255,0.08)',
                borderRadius: '20px', color: active ? '#00d4ff' : '#94a3b8',
                fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
                fontFamily: 'inherit', transition: 'all 0.15s' }}>
              {t}
            </button>
          );
        })}
      </div>
      {filterTickers.length > 0 && (
        <button onClick={() => setFilterTickers([])}
          style={{ fontSize: '0.72rem', color: '#64748b', background: 'none',
            border: 'none', cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>
          ✕ Clear filter
        </button>
      )}
    </div>
  );
}

// ── Earnings table ───────────────────────────────────────────────────────────
function EarningsTab({ from, to, filterTickers }) {
  const [events,  setEvents]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    fetch(`${API_URL}/api/seo/calendar/earnings?from=${from}&to=${to}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => { if (!cancelled) { setEvents(d.events || []); setLoading(false); } })
      .catch(() => { if (!cancelled) { setError('Earnings data unavailable'); setLoading(false); } });
    return () => { cancelled = true; };
  }, [from, to]);

  if (loading) return <LoadingState />;
  if (error)   return <EmptyState msg={error} />;

  const filtered = filterTickers.length > 0
    ? events.filter(e => filterTickers.includes(e.symbol))
    : events;

  if (filtered.length === 0) return (
    <EmptyState msg={filterTickers.length > 0
      ? 'No earnings this week for your watchlist stocks.'
      : 'No earnings scheduled this week.'} />
  );

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
            {['DATE', 'COMPANY', 'TICKER', 'TIME', 'EPS EST', 'EPS ACT'].map(h => (
              <th key={h} style={{ padding: '0.6rem 0.75rem', textAlign: 'left',
                color: '#475569', fontWeight: 600, fontSize: '0.7rem',
                letterSpacing: '0.07em', whiteSpace: 'nowrap' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filtered.map((e, i) => (
            <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)',
              transition: 'background 0.12s' }}
              onMouseEnter={ev => ev.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
              onMouseLeave={ev => ev.currentTarget.style.background = 'transparent'}>
              <td style={{ padding: '0.65rem 0.75rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                {fmtDate(e.date)}
              </td>
              <td style={{ padding: '0.65rem 0.75rem', color: '#f8fafc', fontWeight: 500,
                maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {e.name}
              </td>
              <td style={{ padding: '0.65rem 0.75rem' }}>
                <span style={{ color: '#00d4ff', fontWeight: 700, fontSize: '0.8rem' }}>
                  {e.symbol}
                </span>
              </td>
              <td style={{ padding: '0.65rem 0.75rem', color: '#64748b', whiteSpace: 'nowrap' }}>
                {e.hour === 'BMO' ? '🌅 Pre-market' : e.hour === 'AMO' ? '🌆 After-hours' : '—'}
              </td>
              <td style={{ padding: '0.65rem 0.75rem', color: '#cbd5e1', textAlign: 'right' }}>
                {e.epsEstimate != null ? '$' + Number(e.epsEstimate).toFixed(2) : '—'}
              </td>
              <td style={{ padding: '0.65rem 0.75rem', textAlign: 'right',
                color: e.epsActual == null ? '#64748b'
                  : e.epsActual >= (e.epsEstimate ?? 0) ? '#00ff88' : '#ef4444',
                fontWeight: e.epsActual != null ? 600 : 400 }}>
                {e.epsActual != null ? '$' + Number(e.epsActual).toFixed(2) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Economic table ───────────────────────────────────────────────────────────
function EconomicTab({ from, to }) {
  const [events,  setEvents]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    fetch(`${API_URL}/api/seo/calendar/economic?from=${from}&to=${to}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => { if (!cancelled) { setEvents(d.events || []); setLoading(false); } })
      .catch(() => { if (!cancelled) { setError('Economic calendar unavailable'); setLoading(false); } });
    return () => { cancelled = true; };
  }, [from, to]);

  if (loading) return <LoadingState />;
  if (error)   return <EmptyState msg={error} />;
  if (events.length === 0) return <EmptyState msg="No economic events this week." />;

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
            {['TIME', 'EVENT', 'COUNTRY', 'IMPACT', 'ESTIMATE', 'PREVIOUS'].map(h => (
              <th key={h} style={{ padding: '0.6rem 0.75rem', textAlign: 'left',
                color: '#475569', fontWeight: 600, fontSize: '0.7rem',
                letterSpacing: '0.07em', whiteSpace: 'nowrap' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {events.map((e, i) => (
            <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)',
              transition: 'background 0.12s' }}
              onMouseEnter={ev => ev.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
              onMouseLeave={ev => ev.currentTarget.style.background = 'transparent'}>
              <td style={{ padding: '0.65rem 0.75rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                {fmtTime(e.time)}
              </td>
              <td style={{ padding: '0.65rem 0.75rem', color: '#f8fafc', fontWeight: 500,
                maxWidth: '240px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {e.event}
              </td>
              <td style={{ padding: '0.65rem 0.75rem', color: '#94a3b8' }}>
                {e.country || '—'}
              </td>
              <td style={{ padding: '0.65rem 0.75rem', whiteSpace: 'nowrap' }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 600,
                  color: IMPACT_COLOR[e.impact] || '#64748b' }}>
                  {IMPACT_LABEL[e.impact] || '—'}
                </span>
              </td>
              <td style={{ padding: '0.65rem 0.75rem', color: '#cbd5e1', textAlign: 'right' }}>
                {e.estimate != null ? e.estimate + (e.unit ? ' ' + e.unit : '') : '—'}
              </td>
              <td style={{ padding: '0.65rem 0.75rem', color: '#64748b', textAlign: 'right' }}>
                {e.prev != null ? e.prev + (e.unit ? ' ' + e.unit : '') : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── IPO table ────────────────────────────────────────────────────────────────
function IpoTab({ from, to }) {
  const [events,  setEvents]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    fetch(`${API_URL}/api/seo/calendar/ipo?from=${from}&to=${to}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => { if (!cancelled) { setEvents(d.events || []); setLoading(false); } })
      .catch(() => { if (!cancelled) { setError('IPO calendar unavailable'); setLoading(false); } });
    return () => { cancelled = true; };
  }, [from, to]);

  if (loading) return <LoadingState />;
  if (error)   return <EmptyState msg={error} />;
  if (events.length === 0) return <EmptyState msg="No IPOs scheduled this week." />;

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
            {['DATE', 'COMPANY', 'TICKER', 'EXCHANGE', 'SHARES', 'PRICE', 'STATUS'].map(h => (
              <th key={h} style={{ padding: '0.6rem 0.75rem', textAlign: 'left',
                color: '#475569', fontWeight: 600, fontSize: '0.7rem',
                letterSpacing: '0.07em', whiteSpace: 'nowrap' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {events.map((e, i) => (
            <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)',
              transition: 'background 0.12s' }}
              onMouseEnter={ev => ev.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
              onMouseLeave={ev => ev.currentTarget.style.background = 'transparent'}>
              <td style={{ padding: '0.65rem 0.75rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                {fmtDate(e.date)}
              </td>
              <td style={{ padding: '0.65rem 0.75rem', color: '#f8fafc', fontWeight: 500,
                maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {e.name}
              </td>
              <td style={{ padding: '0.65rem 0.75rem' }}>
                <span style={{ color: '#00d4ff', fontWeight: 700, fontSize: '0.8rem' }}>
                  {e.symbol}
                </span>
              </td>
              <td style={{ padding: '0.65rem 0.75rem', color: '#94a3b8' }}>
                {e.exchange || '—'}
              </td>
              <td style={{ padding: '0.65rem 0.75rem', color: '#cbd5e1', textAlign: 'right' }}>
                {e.shares != null
                  ? (e.shares >= 1e6
                    ? (e.shares / 1e6).toFixed(1) + 'M'
                    : Number(e.shares).toLocaleString())
                  : '—'}
              </td>
              <td style={{ padding: '0.65rem 0.75rem', color: '#cbd5e1', textAlign: 'right' }}>
                {e.price || '—'}
              </td>
              <td style={{ padding: '0.65rem 0.75rem' }}>
                <span style={{ fontSize: '0.72rem', fontWeight: 600, padding: '0.2rem 0.55rem',
                  borderRadius: '20px',
                  background: e.status === 'priced'
                    ? 'rgba(0,255,136,0.1)' : 'rgba(255,170,0,0.1)',
                  color: e.status === 'priced' ? '#00ff88' : '#ffaa00',
                  border: e.status === 'priced'
                    ? '1px solid rgba(0,255,136,0.2)' : '1px solid rgba(255,170,0,0.2)' }}>
                  {e.status || 'expected'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Main CalendarPage ────────────────────────────────────────────────────────
export default function CalendarPage({ isLoggedIn, token, onRequestInvite, onSignIn, onNavigate }) {
  const [activeTab,     setActiveTab]     = useState('earnings');
  const [weekOffset,    setWeekOffset]    = useState(0);
  const [filterTickers, setFilterTickers] = useState([]);

  const { from, to } = getWeekBounds(weekOffset);
  const weekLabel     = fmtWeekLabel(from, to);
  const isThisWeek    = weekOffset === 0;

  return (
    <div>

      {/* ── Header ── */}
      <div style={{ marginBottom: '1.75rem' }}>
        <h1 style={{ margin: '0 0 0.4rem', fontSize: '1.6rem', fontWeight: 900,
          color: '#f8fafc', letterSpacing: '-0.02em' }}>
          Market Calendar
        </h1>
        <div style={{ fontSize: '0.8rem', color: '#475569' }}>
          Earnings · Economic events · IPOs — delayed 15 min · Not investment advice
        </div>
      </div>

      {/* ── Week navigation ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem',
        marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <button onClick={() => setWeekOffset(w => w - 1)}
          style={{ padding: '0.5rem 1rem', background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px',
            color: '#94a3b8', cursor: 'pointer', fontSize: '0.82rem',
            fontFamily: 'inherit', fontWeight: 600 }}>
          ← Prev
        </button>
        {!isThisWeek && (
          <button onClick={() => setWeekOffset(0)}
            style={{ padding: '0.5rem 1rem', background: 'rgba(0,212,255,0.08)',
              border: '1px solid rgba(0,212,255,0.2)', borderRadius: '8px',
              color: '#00d4ff', cursor: 'pointer', fontSize: '0.82rem',
              fontFamily: 'inherit', fontWeight: 600 }}>
            This Week
          </button>
        )}
        <span style={{ fontWeight: 700, color: '#f8fafc', fontSize: '0.95rem' }}>
          {weekLabel}
        </span>
        <button onClick={() => setWeekOffset(w => w + 1)}
          style={{ padding: '0.5rem 1rem', background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px',
            color: '#94a3b8', cursor: 'pointer', fontSize: '0.82rem',
            fontFamily: 'inherit', fontWeight: 600 }}>
          Next →
        </button>
      </div>

      {/* ── Layout: main content + watchlist sidebar ── */}
      <div style={{ display: 'grid',
        gridTemplateColumns: 'minmax(0,1fr) 200px',
        gap: '1.25rem', alignItems: 'start' }}>

        {/* ── Main panel ── */}
        <div>
          {/* Tab bar */}
          <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.08)',
            marginBottom: '1.25rem', gap: 0 }}>
            {TABS.map(tab => {
              const isActive = activeTab === tab.id;
              return (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  style={{ padding: '0.7rem 1.1rem', background: 'none', border: 'none',
                    borderBottom: isActive ? '2px solid #00d4ff' : '2px solid transparent',
                    color: isActive ? '#00d4ff' : '#64748b',
                    fontWeight: isActive ? 700 : 500, cursor: 'pointer',
                    fontSize: '0.83rem', fontFamily: 'inherit',
                    whiteSpace: 'nowrap', marginBottom: '-1px',
                    transition: 'color 0.15s' }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.color = '#94a3b8'; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.color = '#64748b'; }}>
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Tab content */}
          <div style={{ background: 'rgba(255,255,255,0.02)',
            border: '1px solid rgba(255,255,255,0.05)',
            borderRadius: '12px', overflow: 'hidden' }}>
            {activeTab === 'earnings' && (
              <>
                {/* Ticker search — COMING SOON. Deliberately a static placeholder,
                    not the real TickerSearchInput component: the underlying bulk
                    Earnings feed is still empty even after the URL fix this session
                    (see CALENDAR-PIPELINE tracked entry) — wiring a live search box
                    against data that always returns nothing would be worse than not
                    having it, implying real companies have zero earnings. Once that
                    pipeline lands, this becomes: <TickerSearchInput onSelect={(t) =>
                    setFilterTickers([t])} /> — reuses the filterTickers state the
                    watchlist buttons already drive, no new mechanism needed.
                    Not added to Economic (no ticker/symbol field on that data at all —
                    country-level macro events, not company-specific) or IPO (already
                    works via browsing, doesn't need a ticker filter). */}
                <div style={{ padding: '0.85rem 1.1rem', borderBottom: '1px solid rgba(255,255,255,0.05)',
                  display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <div style={{ flex: '1 1 220px', padding: '0.55rem 0.9rem',
                    background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: '8px', color: '#374151', fontSize: '0.82rem', cursor: 'not-allowed' }}>
                    Search ticker to filter earnings…
                  </div>
                  <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#a78bfa',
                    background: 'rgba(167,139,250,0.1)', border: '1px solid rgba(167,139,250,0.25)',
                    borderRadius: '20px', padding: '0.25rem 0.7rem', whiteSpace: 'nowrap' }}>
                    Coming Soon
                  </span>
                </div>
                <EarningsTab from={from} to={to} filterTickers={filterTickers} />
              </>
            )}
            {activeTab === 'economic' && (
              <EconomicTab from={from} to={to} />
            )}
            {activeTab === 'ipo' && (
              <IpoTab from={from} to={to} />
            )}
          </div>

          {/* Disclaimer */}
          <div style={{ marginTop: '1rem', fontSize: '0.7rem', color: '#475569', lineHeight: 1.6 }}>
            ⚠️ Data delayed approximately 15 minutes. Calendar events are for informational purposes only and do not constitute investment advice. Always conduct your own research.
          </div>
        </div>

        {/* ── Watchlist sidebar ── */}
        <WatchlistPanel
          isLoggedIn={isLoggedIn}
          token={token}
          filterTickers={filterTickers}
          setFilterTickers={setFilterTickers}
          onRequestInvite={onRequestInvite}
          onSignIn={onSignIn}
        />
      </div>
    </div>
  );
}
